import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, BadgeCheck, Snowflake, FlaskConical } from "lucide-react";
import { vimg } from "@/lib/api";

const SpecRow = ({ label, value }) => (
  <div className="flex justify-between gap-6 py-2.5 border-b border-[#E4E4E7] last:border-0">
    <span className="font-mono-clinical text-[10px] tracking-[0.15em] uppercase text-[#71717A]">{label}</span>
    <span className="text-xs text-[#09090B] text-right font-medium">{value}</span>
  </div>
);

export const ProductDetail = ({ product: p, verified, onClose, onAdd }) => (
  <AnimatePresence>
    {p && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm"
        />
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            data-testid="product-detail-modal"
            className="pointer-events-auto w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white border border-[#E4E4E7]"
          >
            <div className="grid md:grid-cols-2">
              <div className="relative min-h-[320px] md:min-h-[480px] group/imgs bg-[#0A0A0C]">
                <img src={vimg(p.image_url)} alt={`${p.name} research vial`} className="absolute inset-0 w-full h-full object-cover" />
                {p.structure_url && (
                  <img
                    src={vimg(p.structure_url)}
                    alt={`${p.name} molecular structure`}
                    data-testid="detail-structure-img"
                    className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover/imgs:opacity-100 transition-opacity duration-700"
                  />
                )}
                {p.structure_url && (
                  <span className="absolute bottom-3 left-3 font-mono-clinical text-[9px] tracking-[0.2em] uppercase text-white/70 bg-black/50 px-2 py-1">
                    Hover — molecular structure
                  </span>
                )}
              </div>

              <div className="relative p-8">
                <button onClick={onClose} data-testid="detail-close" className="absolute top-4 right-4 p-2 text-[#71717A] hover:text-[#09090B] transition-colors">
                  <X className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-2 font-mono-clinical text-[11px] tracking-[0.2em] uppercase mb-3">
                  <span className="text-[#6D28D9] flex items-center gap-1.5">
                    {p.code}
                    {verified && <BadgeCheck className="w-3.5 h-3.5 text-[#6D28D9]" strokeWidth={1.5} />}
                  </span>
                  <span className="text-[#A1A1AA]">{p.purity}</span>
                </div>
                <h3 className="font-display text-3xl font-bold uppercase tracking-tight text-[#09090B]">
                  {p.name} <span className="text-[#A1A1AA] text-xl">{p.size}</span>
                </h3>
                <p className="font-mono-clinical text-[10px] tracking-[0.2em] uppercase text-[#71717A] mt-2">{p.category}</p>

                <p className="text-sm text-[#52525B] leading-relaxed mt-5">{p.description}</p>

                <div className="mt-6">
                  <p className="font-mono-clinical text-[10px] tracking-[0.25em] uppercase text-[#6D28D9] mb-2 flex items-center gap-1.5">
                    <FlaskConical className="w-3.5 h-3.5" strokeWidth={1.5} /> Specifications
                  </p>
                  <div className="border-t border-[#E4E4E7]">
                    <SpecRow label="Product code" value={p.code} />
                    <SpecRow label="Content" value={p.size} />
                    <SpecRow label="Purity (HPLC)" value={p.purity} />
                    {p.form && <SpecRow label="Form" value={p.form} />}
                    {p.sequence && <SpecRow label="Sequence" value={<span className="font-mono-clinical break-all">{p.sequence}</span>} />}
                  </div>
                </div>

                {p.storage && (
                  <div className="mt-5 border border-[#6D28D9]/25 bg-[#F3F0FF] p-4">
                    <p className="font-mono-clinical text-[10px] tracking-[0.25em] uppercase text-[#6D28D9] mb-2 flex items-center gap-1.5">
                      <Snowflake className="w-3.5 h-3.5" strokeWidth={1.5} /> Storage
                    </p>
                    <p className="text-xs text-[#52525B] leading-relaxed" data-testid="detail-storage">{p.storage}</p>
                  </div>
                )}

                <div className="flex items-center justify-between mt-7 pt-5 border-t border-[#E4E4E7]">
                  <span className="font-mono-clinical text-xl text-[#09090B]">${Number(p.price).toFixed(0)}</span>
                  <button
                    onClick={() => onAdd(p)}
                    data-testid={`detail-add-${p.code}`}
                    className="flex items-center gap-2 px-6 py-3 bg-[#6D28D9] text-white font-mono-clinical text-[11px] tracking-[0.15em] uppercase font-bold hover:bg-[#5B21B6] transition-colors duration-300"
                  >
                    Add to Cart <Plus className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[10px] text-[#A1A1AA] mt-4 leading-relaxed">
                  For laboratory research use only. Not for human or veterinary use.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </>
    )}
  </AnimatePresence>
);
